export interface ImageMeta {
  width: number
  height: number
  bytes: number
}

export interface RasterPreflightLimits {
  maxFileBytes: number
  maxImagePixels: number
  maxLongestSide?: number
}

export type SupportedRasterFormat = 'jpeg' | 'png' | 'webp'

export type FilePreflightCode =
  | 'unsupported'
  | 'file_too_large'
  | 'invalid_container'
  | 'too_many_pixels'
  | 'animated_unsupported'
  | 'decode_failed'

export class FilePreflightError extends Error {
  constructor(readonly code: FilePreflightCode) {
    super(code)
    this.name = 'FilePreflightError'
  }
}

export interface DecodedImage {
  width: number
  height: number
  close: () => void
}

const MAX_CONTAINER_CHUNKS = 4_096
const JPEG_SOF = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7,
  0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
])

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  return String.fromCharCode(...bytes.subarray(offset, offset + length))
}

function uint16be(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] ?? 0) << 8) | (bytes[offset + 1] ?? 0)
}

function uint16le(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0) | ((bytes[offset + 1] ?? 0) << 8)
}

function uint24le(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0)
    | ((bytes[offset + 1] ?? 0) << 8)
    | ((bytes[offset + 2] ?? 0) << 16)
}

function uint32be(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] ?? 0) * 0x1000000)
    + ((bytes[offset + 1] ?? 0) << 16)
    + ((bytes[offset + 2] ?? 0) << 8)
    + (bytes[offset + 3] ?? 0)
}

function uint32le(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0)
    + ((bytes[offset + 1] ?? 0) << 8)
    + ((bytes[offset + 2] ?? 0) << 16)
    + ((bytes[offset + 3] ?? 0) * 0x1000000)
}

function checkedDimensions(
  width: number,
  height: number,
  limits: RasterPreflightLimits,
): { width: number; height: number } {
  const pixels = width * height
  if (
    !Number.isSafeInteger(width) ||
    !Number.isSafeInteger(height) ||
    width < 1 ||
    height < 1 ||
    !Number.isSafeInteger(pixels)
  ) {
    throw new FilePreflightError('invalid_container')
  }
  if (
    pixels > limits.maxImagePixels ||
    (limits.maxLongestSide !== undefined && Math.max(width, height) > limits.maxLongestSide)
  ) {
    throw new FilePreflightError('too_many_pixels')
  }
  return { width, height }
}

function parseJpeg(
  bytes: Uint8Array,
  limits: RasterPreflightLimits,
): { width: number; height: number } {
  if (
    bytes.length < 6 ||
    bytes[0] !== 0xff ||
    bytes[1] !== 0xd8 ||
    bytes.at(-2) !== 0xff ||
    bytes.at(-1) !== 0xd9
  ) {
    throw new FilePreflightError('invalid_container')
  }

  let offset = 2
  let dimensions: { width: number; height: number } | null = null
  let hasStartOfScan = false
  let segments = 0
  while (offset < bytes.length - 2) {
    if (bytes[offset] !== 0xff) throw new FilePreflightError('invalid_container')
    while (offset < bytes.length - 2 && bytes[offset] === 0xff) offset += 1
    if (++segments > MAX_CONTAINER_CHUNKS || offset >= bytes.length - 2) {
      throw new FilePreflightError('invalid_container')
    }
    const marker = bytes[offset] ?? 0
    offset += 1
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) continue
    if (offset + 2 > bytes.length - 2) throw new FilePreflightError('invalid_container')
    const length = uint16be(bytes, offset)
    if (length < 2 || offset + length > bytes.length - 2) {
      throw new FilePreflightError('invalid_container')
    }
    if (marker === 0xda) {
      if (!dimensions || length < 6) throw new FilePreflightError('invalid_container')
      hasStartOfScan = true
      break
    }
    if (JPEG_SOF.has(marker)) {
      if (length < 7 || dimensions) throw new FilePreflightError('invalid_container')
      dimensions = checkedDimensions(
        uint16be(bytes, offset + 5),
        uint16be(bytes, offset + 3),
        limits,
      )
    }
    offset += length
  }
  if (!dimensions || !hasStartOfScan) throw new FilePreflightError('invalid_container')
  return dimensions
}

function parsePng(
  bytes: Uint8Array,
  limits: RasterPreflightLimits,
): { width: number; height: number } {
  if (
    bytes.length < 45 ||
    ascii(bytes, 1, 3) !== 'PNG' ||
    bytes[0] !== 0x89 ||
    bytes[4] !== 0x0d ||
    bytes[5] !== 0x0a ||
    bytes[6] !== 0x1a ||
    bytes[7] !== 0x0a
  ) {
    throw new FilePreflightError('invalid_container')
  }
  let offset = 8
  let chunks = 0
  let dimensions: { width: number; height: number } | null = null
  let hasImageData = false
  while (offset < bytes.length) {
    if (++chunks > MAX_CONTAINER_CHUNKS || offset + 12 > bytes.length) {
      throw new FilePreflightError('invalid_container')
    }
    const length = uint32be(bytes, offset)
    const type = ascii(bytes, offset + 4, 4)
    const next = offset + 12 + length
    if (!Number.isSafeInteger(next) || next > bytes.length) {
      throw new FilePreflightError('invalid_container')
    }
    if (chunks === 1) {
      if (type !== 'IHDR' || length !== 13) throw new FilePreflightError('invalid_container')
      dimensions = checkedDimensions(
        uint32be(bytes, offset + 8),
        uint32be(bytes, offset + 12),
        limits,
      )
    } else if (type === 'IHDR') {
      throw new FilePreflightError('invalid_container')
    }
    if (type === 'acTL' || type === 'fcTL' || type === 'fdAT') {
      throw new FilePreflightError('animated_unsupported')
    }
    if (type === 'IDAT') hasImageData = true
    if (type === 'IEND') {
      if (length !== 0 || next !== bytes.length || !dimensions || !hasImageData) {
        throw new FilePreflightError('invalid_container')
      }
      return dimensions
    }
    offset = next
  }
  throw new FilePreflightError('invalid_container')
}

function parseWebpBitstream(
  bytes: Uint8Array,
  type: string,
  offset: number,
  length: number,
  limits: RasterPreflightLimits,
): { width: number; height: number } | null {
  if (type === 'VP8L' && length >= 5 && bytes[offset] === 0x2f) {
    const b1 = bytes[offset + 1] ?? 0
    const b2 = bytes[offset + 2] ?? 0
    const b3 = bytes[offset + 3] ?? 0
    const b4 = bytes[offset + 4] ?? 0
    return checkedDimensions(
      1 + b1 + ((b2 & 0x3f) << 8),
      1 + (b2 >> 6) + (b3 << 2) + ((b4 & 0x0f) << 10),
      limits,
    )
  }
  if (
    type === 'VP8 ' &&
    length >= 10 &&
    bytes[offset + 3] === 0x9d &&
    bytes[offset + 4] === 0x01 &&
    bytes[offset + 5] === 0x2a
  ) {
    return checkedDimensions(
      uint16le(bytes, offset + 6) & 0x3fff,
      uint16le(bytes, offset + 8) & 0x3fff,
      limits,
    )
  }
  return null
}

function parseWebp(
  bytes: Uint8Array,
  limits: RasterPreflightLimits,
): { width: number; height: number } {
  if (
    bytes.length < 20 ||
    ascii(bytes, 0, 4) !== 'RIFF' ||
    ascii(bytes, 8, 4) !== 'WEBP' ||
    uint32le(bytes, 4) + 8 !== bytes.length
  ) {
    throw new FilePreflightError('invalid_container')
  }
  let offset = 12
  let chunks = 0
  let canvas: { width: number; height: number } | null = null
  let bitstream: { width: number; height: number } | null = null
  while (offset < bytes.length) {
    if (++chunks > MAX_CONTAINER_CHUNKS || offset + 8 > bytes.length) {
      throw new FilePreflightError('invalid_container')
    }
    const type = ascii(bytes, offset, 4)
    const length = uint32le(bytes, offset + 4)
    const next = offset + 8 + length + (length % 2)
    if (!Number.isSafeInteger(next) || next > bytes.length) {
      throw new FilePreflightError('invalid_container')
    }
    if (type === 'VP8X') {
      if (length !== 10 || canvas) throw new FilePreflightError('invalid_container')
      if (((bytes[offset + 8] ?? 0) & 0x02) !== 0) {
        throw new FilePreflightError('animated_unsupported')
      }
      canvas = checkedDimensions(
        uint24le(bytes, offset + 12) + 1,
        uint24le(bytes, offset + 15) + 1,
        limits,
      )
    } else if (type === 'ANIM' || type === 'ANMF') {
      throw new FilePreflightError('animated_unsupported')
    } else if (type === 'VP8 ' || type === 'VP8L') {
      if (bitstream) throw new FilePreflightError('invalid_container')
      bitstream = parseWebpBitstream(bytes, type, offset + 8, length, limits)
      if (!bitstream) throw new FilePreflightError('invalid_container')
    }
    offset = next
  }
  if (offset !== bytes.length || !bitstream) throw new FilePreflightError('invalid_container')
  if (canvas && (canvas.width !== bitstream.width || canvas.height !== bitstream.height)) {
    throw new FilePreflightError('invalid_container')
  }
  return canvas ?? bitstream
}

export function preflightRasterBytes(
  bytes: Uint8Array,
  format: SupportedRasterFormat,
  limits: RasterPreflightLimits,
): ImageMeta {
  const dimensions = format === 'png'
    ? parsePng(bytes, limits)
    : format === 'webp'
      ? parseWebp(bytes, limits)
      : parseJpeg(bytes, limits)
  return { ...dimensions, bytes: bytes.byteLength }
}

export function hasSupportedRasterFileMetadata(
  file: Pick<File, 'name' | 'type' | 'size'>,
  limits: RasterPreflightLimits,
): boolean {
  if (file.size > limits.maxFileBytes) return false
  const type = file.type.toLowerCase()
  if (type === 'image/jpeg' || type === 'image/png' || type === 'image/webp') return true
  const name = file.name.toLowerCase()
  return ['.jpg', '.jpeg', '.png', '.webp'].some((extension) => name.endsWith(extension))
}

export async function preflightRasterFile(
  file: File,
  limits: RasterPreflightLimits,
): Promise<ImageMeta> {
  if (!hasSupportedRasterFileMetadata(file, limits)) {
    throw new FilePreflightError(file.size > limits.maxFileBytes ? 'file_too_large' : 'unsupported')
  }
  const bytes = new Uint8Array(await file.arrayBuffer())
  const format: SupportedRasterFormat = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')
    ? 'png'
    : file.type === 'image/webp' || file.name.toLowerCase().endsWith('.webp')
      ? 'webp'
      : 'jpeg'
  return { ...preflightRasterBytes(bytes, format, limits), bytes: file.size }
}

export async function inspectRasterFile(
  file: File,
  limits: RasterPreflightLimits,
  decode: (source: ImageBitmapSource) => Promise<DecodedImage> = createImageBitmap,
): Promise<ImageMeta> {
  const meta = await preflightRasterFile(file, limits)
  await verifyRasterDecode(file, meta, decode)
  return meta
}

export async function verifyRasterDecode(
  file: File,
  meta: ImageMeta,
  decode: (source: ImageBitmapSource) => Promise<DecodedImage> = createImageBitmap,
): Promise<void> {
  let bitmap: DecodedImage
  try {
    bitmap = await decode(file)
  } catch {
    throw new FilePreflightError('decode_failed')
  }
  try {
    if (bitmap.width !== meta.width || bitmap.height !== meta.height) {
      throw new FilePreflightError('invalid_container')
    }
  } finally {
    bitmap.close()
  }
}
